// Admin user-management DTOs (over the existing users table). The password hash is NEVER part of
// any DTO. Role drives ROLE_AUDIENCE (staff/clinic/patient/hr); clinic/patient accounts carry a
// clinicId/caseId.
import { z } from 'zod';
import { ROLES } from '../permissions.js';

export interface AdminUser {
  id: string;
  username: string;
  email: string | null;
  displayName: string;
  role: string;
  isActive: boolean;
  clinicId: string | null;
  caseId: string | null;
  createdAt: string;
  updatedAt: string;
}

export const createUserSchema = z.object({
  username: z.string().trim().min(3).max(60),
  password: z.string().min(8).max(200),
  role: z.enum(ROLES),
  displayName: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(160).nullish(),
  clinicId: z.string().min(1).nullish(),
  caseId: z.string().min(1).nullish(),
});
export type CreateUserRequest = z.infer<typeof createUserSchema>;

export const updateUserSchema = z
  .object({
    role: z.enum(ROLES).optional(),
    displayName: z.string().trim().min(1).max(120).optional(),
    email: z.string().trim().email().max(160).nullish(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export type UpdateUserRequest = z.infer<typeof updateUserSchema>;

export const resetPasswordSchema = z.object({ password: z.string().min(8).max(200) });
export type ResetPasswordRequest = z.infer<typeof resetPasswordSchema>;

export interface UserListQuery {
  search?: string;
  role?: string;
  active?: boolean;
  /**
   * Who to list. Defaults to 'staff' — people who work at the lab.
   *
   * Clinic logins and per-case patient logins are accounts too, but they are provisioned and reset
   * from where they belong (a clinic's Portal panel, a case's patient access card). Listing them
   * here invited someone to reset a doctor's password from the staff admin, which is not where
   * anybody would look for it, and buried the eight real staff among them.
   */
  audience?: 'staff' | 'all';
  limit: number;
  offset: number;
}
